import { useState, type FormEvent } from 'react';
import {
  Sparkles,
  Save,
  Trash2,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Loader2,
  PlayCircle,
  Key,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useAIProviders,
  useSaveAIProvider,
  useRemoveAIProvider,
  useTestAIProvider,
  type AIProviderStatus,
  type AIProviderCatalogEntry,
} from '@/hooks/usePlatform';
import { ApiError } from '@/lib/api-client';
import { useConfirm } from '@/components/ConfirmDialog';
import { cn, formatDate } from '@/lib/utils';

export function AIProvidersPage() {
  const { data, isPending } = useAIProviders();

  if (isPending) return <div className="text-muted-foreground">Yükleniyor…</div>;
  if (!data) return <div className="text-destructive">Veri yüklenemedi</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Sparkles className="w-5 h-5" /> AI Sağlayıcılar
        </h1>
        <p className="text-muted-foreground text-sm">
          İçerik üretimi için kullanılacak API key'leri. Secret'lar AES-GCM ile şifrelenmiş olarak
          D1'de saklanır. AIMLAPI varsayılan sağlayıcı — tek key ile 400+ modele erişim sağlar.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {data.catalog.map((entry) => {
          const status = data.providers.find((p) => p.provider === entry.id);
          return status ? <ProviderCard key={entry.id} entry={entry} status={status} /> : null;
        })}
      </div>
    </div>
  );
}

function ProviderCard({
  entry,
  status,
}: {
  entry: AIProviderCatalogEntry;
  status: AIProviderStatus;
}) {
  const save = useSaveAIProvider();
  const remove = useRemoveAIProvider();
  const test = useTestAIProvider();
  const confirm = useConfirm();

  const [newKey, setNewKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [testMessage, setTestMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaveMessage(null);
    setTestMessage(null);
    try {
      await save.mutateAsync({ provider: entry.id, key: newKey });
      setNewKey('');
      setSaveMessage('Kaydedildi ✓');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kaydedilemedi');
    }
  };

  const onTest = async () => {
    setTestMessage(null);
    setError(null);
    try {
      const res = await test.mutateAsync({
        provider: entry.id,
        key: newKey.length >= 8 ? newKey : undefined,
      });
      setTestMessage({ ok: res.ok, text: res.message });
    } catch (err) {
      setTestMessage({
        ok: false,
        text: err instanceof ApiError ? err.message : 'Test başarısız',
      });
    }
  };

  const onRemove = async () => {
    const ok = await confirm({
      title: `${entry.label} anahtarı kaldırılsın?`,
      description: 'Anahtar kalıcı olarak silinir. İstediğin zaman yeniden yapılandırabilirsin.',
      confirmText: 'Kaldır',
      danger: true,
    });
    if (!ok) return;
    await remove.mutateAsync(entry.id);
  };

  const configured = status.configured && status.enabled;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <CardTitle className="text-base">{entry.label}</CardTitle>
            {entry.is_default && (
              <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary uppercase tracking-wider">
                Varsayılan
              </span>
            )}
            <StatusBadge configured={configured} />
          </div>
          <a
            href={entry.docs_url}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1"
          >
            Key al <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <CardDescription className="text-xs">{entry.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {configured && (
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-2">
              <Key className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="font-mono">{status.key_hint}</span>
            </span>
            <div className="flex items-center gap-2">
              {status.updated_at && (
                <span className="text-muted-foreground hidden sm:inline">
                  {formatDate(status.updated_at)}
                </span>
              )}
              <Button size="sm" variant="ghost" onClick={onRemove} disabled={remove.isPending}>
                <Trash2 className="w-3.5 h-3.5" /> Sil
              </Button>
            </div>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor={`${entry.id}-key`}>
              API Key {configured && <span className="text-xs text-muted-foreground">(yenilemek için yaz)</span>}
            </Label>
            <Input
              id={`${entry.id}-key`}
              type="password"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              placeholder={configured ? '••••••••••••••••' : entry.key_placeholder || 'API key'}
              autoComplete="off"
            />
          </div>

          {error && (
            <div className="text-xs text-destructive bg-destructive/10 rounded px-2 py-1.5">{error}</div>
          )}
          {saveMessage && (
            <div className="text-xs text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 rounded px-2 py-1.5">
              {saveMessage}
            </div>
          )}
          {testMessage && (
            <div
              className={cn(
                'text-xs rounded px-2 py-1.5 flex items-start gap-1.5',
                testMessage.ok
                  ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                  : 'bg-destructive/10 text-destructive',
              )}
            >
              {testMessage.ok ? (
                <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              ) : (
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              )}
              {testMessage.text}
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <Button type="submit" size="sm" disabled={save.isPending || newKey.length < 8}>
              {save.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {configured ? 'Güncelle' : 'Kaydet'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onTest}
              disabled={test.isPending || (!configured && newKey.length < 8)}
            >
              {test.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
              Test
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ configured }: { configured: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium',
        configured
          ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
          : 'bg-muted text-muted-foreground',
      )}
    >
      {configured ? 'YAPILANDIRILDI' : 'YOK'}
    </span>
  );
}
